/**
 * Copy registry — the *only* surface a theme is allowed to rewrite.
 *
 * Why a registry instead of free strings:
 *   1. Prevents themes from "renaming" copy the app no longer ships.
 *   2. Lets us classify each key by risk so destructive actions are never
 *      reskinnable. A theme that tries to rename "Delete Device" to "Banish
 *      to the Shadow" gets rejected at validation time.
 *   3. Gives the AI generator (Phase 2) an explicit allowlist with canonical
 *      English to anchor the prompt.
 *
 * Risk levels:
 *   - cosmetic    — pure decoration (e.g. dashboard greeting). Safe.
 *   - navigation  — sidebar / top-level page names. Safe-ish; reveal hint
 *                   helps users translate back.
 *   - action      — primary CTA buttons that perform a real operation
 *                   (Enroll Device, Add User, Save). Reveal hint mandatory.
 *   - destructive — Delete, Wipe, Unenroll, Revoke, Remove. NEVER overridable.
 *                   Intentionally absent from this map.
 *
 * Adding a new key:
 *   1. Append it below with the canonical English copy and a risk level.
 *   2. Replace the literal at the call site with `<Copy k="..." />` or
 *      `useCopy("...")`.
 *   3. The schema test will fail loudly if a preset references an unknown key.
 */

export type CopyRisk = "cosmetic" | "navigation" | "action";

export interface CopyEntry {
  /** English source-of-truth label. Always shown to screen readers. */
  canonical: string;
  /** Short note on what this label refers to — fed into the AI prompt. */
  purpose?: string;
  risk: CopyRisk;
  /** Max chars allowed for an override at this site. Defaults to 32. */
  maxLength?: number;
}

export const COPY_REGISTRY = {
  // ---------- Navigation ----------
  "nav.dashboard":      { canonical: "Dashboard",      risk: "navigation", purpose: "Top-level home view" },
  "nav.devices":        { canonical: "Devices",        risk: "navigation", purpose: "Managed endpoints list" },
  "nav.applications":   { canonical: "Applications",   risk: "navigation", purpose: "Software catalog" },
  "nav.configurations": { canonical: "Configurations", risk: "navigation", purpose: "MDM profile library" },
  "nav.tags":           { canonical: "Tags",           risk: "navigation", purpose: "Device grouping labels" },
  "nav.users":          { canonical: "Users",          risk: "navigation", purpose: "Console + directory users" },
  "nav.settings":       { canonical: "Settings",       risk: "navigation", purpose: "Tenant configuration" },

  // ---------- Page titles ----------
  "page.dashboard.title":    { canonical: "Dashboard",          risk: "navigation" },
  "page.devices.title":      { canonical: "Devices",            risk: "navigation" },
  "page.applications.title": { canonical: "Applications",       risk: "navigation" },
  "page.tags.title":         { canonical: "Tags",               risk: "navigation" },

  // ---------- Primary actions ----------
  "action.enrollDevice":   { canonical: "Enroll Device",   risk: "action", purpose: "Begin device enrollment flow" },
  "action.addApplication": { canonical: "Add Application", risk: "action", purpose: "Open the add-app wizard" },
  "action.createTag":      { canonical: "Create Tag",      risk: "action", purpose: "Open the create-tag dialog" },
  "action.addUser":        { canonical: "Add User",        risk: "action", purpose: "Invite or create a new user" },
  "action.save":           { canonical: "Save",            risk: "action", purpose: "Commit pending changes" },
  "action.cancel":         { canonical: "Cancel",          risk: "action", purpose: "Discard pending changes" },

  // ---------- Empty-state CTAs ----------
  "empty.devices.cta":      { canonical: "Enroll your first device",     risk: "action", maxLength: 40 },
  "empty.applications.cta": { canonical: "Add your first application",   risk: "action", maxLength: 40 },
  "empty.tags.cta":         { canonical: "Create your first tag",        risk: "action", maxLength: 40 },
} as const satisfies Record<string, CopyEntry>;

export type CopyKey = keyof typeof COPY_REGISTRY;

export const COPY_KEYS = Object.keys(COPY_REGISTRY) as CopyKey[];

export function getCanonical(key: CopyKey): string {
  return COPY_REGISTRY[key].canonical;
}

export function getMaxLength(key: CopyKey): number {
  const entry = COPY_REGISTRY[key] as CopyEntry;
  return entry.maxLength ?? 32;
}

export function isCopyKey(value: string): value is CopyKey {
  return Object.prototype.hasOwnProperty.call(COPY_REGISTRY, value);
}
